import React, { useEffect, useState } from 'react';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { CTAProps } from './CTAButton';
import { getProductServiceVariantId, getSelectedServiceSuggestion } from '../utils/product-service';
import { hasReachedMaxQty } from '../../../../utils/checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../../../../utils/checkMaxQty/showMaxQtyAlert';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from '../../../../utils/shopify/getStoreCurrency';
import { transformShopifyProductForTracking } from '../../../../utils/cart/transformShopifyProductForTracking';
import { getInsight, postAlgoliaAddToCart } from '../../../../utils/algolia/search-insights';
import { AttachAddToCartSlideout } from '../../attachAddToCartSlideout/AttachAddToCartSlideout';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { ActionButton } from '@jbhi-fi/components/src/ActionButton';
import { CartActionState } from '@jbhi-fi/components/src/CartProvider/types';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { getMarketplaceTrackingValuesFromShopify } from '../../../../utils/event-tracking/globalTrackingHelpers';
import { getCartLoading, setCartLoading, transformBonusItemsForCart } from '../../../../utils/cart/cartUtils';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { addServiceOffering } from '../../../../utils/cart/addServiceOffering';
import { getServiceOfferings, setServiceOfferings } from '../../../../utils/parseServiceOfferings';
import { ServiceIds } from '../../../../utils/constants/services';
import { getMarketplacePropertiesBySeller } from '../../../../utils/cart/refreshTieredProductDetails';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { ServiceOffering } from '@jbhi-fi/apis/src/services/types';
import { klaviyoAddedToCart } from '../../../../utils/klaviyo';
import { getCartApiResponse } from '../../../../utils/cartApi';
import { useCartContext } from '../../../../utils/cart/context/CartContext';
import { updateCart } from '../../../../utils/cart/cartUtils';

export const ADD_TO_CART_CONFIRMATION_DURATION_MS = 2000;

const storefrontClient = new ShopifyStorefrontClient();

export const AddToCart = ({
  productCartData,
  product,
  gtmProduct,
  quantity = 1,
  ecommerceTrackingProps,
}: CTAProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [buttonState, setButtonState] = useState<CartActionState>(CartActionState.Default);
  const [modalVisible, setIsModalVisible] = useState<boolean>(false);
  const { shopify, productMetafields, marketplaceSellers } = useShopifyProduct();
  const productSku = shopify?.variants[0]?.sku;
  const productVariantId = product.variants[0].id;
  const { products: [algoliaProduct] = [] } = useProducts({ productSkus: [productSku] });
  const { toggleMiniCart } = useCartContext();
  const reviewCount = productMetafields?.Display?.NumberOfProductReviews;
  const reviewScore = productMetafields?.Display?.AverageProductReviewRating;
  const isMarketplaceProduct = ShopifyProductTransformer.isMarketplaceProduct({ product: shopify });
  const isEligibleForServices = !!productMetafields?.Product?.EligibleForServices && !isMarketplaceProduct;
  const isAttachSlideoutEnabled = window.Jbhifi?.attachConfig?.enabledPages?.pdp && !isMarketplaceProduct;
  const shouldShowSlideout = isAttachSlideoutEnabled && algoliaProduct;
  const { products: bonusProducts } = useProducts({ productSkus: productMetafields?.Product?.FreeBonusSkus });

  useEffect(() => {
    if (buttonState !== CartActionState.Complete) return;
    const timeout = setTimeout(() => setButtonState(CartActionState.Default), ADD_TO_CART_CONFIRMATION_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [buttonState]);

  const onModalClose = () => {
    setIsModalVisible(false);
    window.zE?.('webWidget', 'show');
  };

  const addToCartTrackingData = {
    location: 'pdp',
    currency: getStoreCurrency(),
    addedItems: {
      ...transformShopifyProductForTracking({
        item: productCartData,
        listName: ecommerceTrackingProps?.listName ?? 'pdp',
        listSlot: ecommerceTrackingProps?.listSlot,
        listServiceConfig: ecommerceTrackingProps?.listServiceConfig,
        reviewCount,
        reviewScore,
      }),
      ...getMarketplaceTrackingValuesFromShopify({
        product: shopify,
        marketplaceSellers,
        onlineProduct: productMetafields,
      }),
    },
  };

  const addToCart = async () => {
    if (getCartLoading()) {
      return;
    }

    if (hasReachedMaxQty(productVariantId, quantity || 1)) {
      showMaxQtyAlert();
      return;
    }

    setIsLoading(true);
    setCartLoading(true);
    if (shouldShowSlideout) toggleMiniCart(false);

    window.zE?.('webWidget', 'hide');

    const properties = {
      _availabilityCanPreOrder: productMetafields?.Availability?.CanPreOrder,
      _availabilityFulfilment: productMetafields?.Availability?.Fulfilment,
      _maxQty: productMetafields?.Availability?.Fulfilment === 'Digital' ? 1 : productMetafields.Product?.LimitPerOrder,
      _priceAtc: shopify?.variants[0]?.price,
      _itemListName: ecommerceTrackingProps?.listName || undefined,
      _itemListSlot: ecommerceTrackingProps?.listSlot || undefined,
      _itemListServiceConfig: ecommerceTrackingProps?.listServiceConfig || undefined,
      _productGroup: productMetafields?.ProductGroup?.Id,
      _eligibleForServices: isEligibleForServices,
    };

    const marketplaceSeller = ShopifyProductTransformer.toMarketplaceSeller({
      product: shopify,
      marketplaceSellers,
      onlineProduct: productMetafields,
    });

    const marketplaceProperties = getMarketplacePropertiesBySeller(marketplaceSeller);
    const attributes = filterAndMapAttributes({ ...properties, ...marketplaceProperties });
    const primaryItem = { variantId: productVariantId, quantity, attributes };
    const bonusLineItems = transformBonusItemsForCart(bonusProducts, ecommerceTrackingProps?.listName || undefined);
    let updatedCart = await storefrontClient.addMultipleToCart([primaryItem, ...bonusLineItems]);

    // Update Service Offerings
    if (updatedCart) {
      const cartApiResponse = await getCartApiResponse(updatedCart);
      if (cartApiResponse) setServiceOfferings(cartApiResponse, updatedCart);
    }

    // Add selected service offering to cart
    const suggestion = getSelectedServiceSuggestion();
    if (suggestion?.services?.[0].offerings?.length) {
      const serviceType = suggestion.services[0].id;
      const offering = suggestion.services[0].offerings[0];
      const serviceOfferings = getServiceOfferings();
      const addedItem = updatedCart?.items.find(item => item.id === productVariantId.toString());
      if (!addedItem) return;
      const serviceOffering = (serviceOfferings?.[addedItem.line_id] as Array<ServiceOffering>)?.find(
        service => service.typeId === serviceType && service.id.includes(offering.id)
      );

      if (!serviceOffering?.shouldRender) return;

      const shopifyVariantId = await getProductServiceVariantId(serviceOffering.sku);
      if (!shopifyVariantId) return;

      updatedCart = await addServiceOffering({
        variantId: shopifyVariantId,
        location: 'pdp',
        serviceData: serviceOffering,
        primaryLineItem: addedItem,
        shouldDrawCart: false,
      });
    }

    // Show Slideout
    setIsModalVisible(true);
    setButtonState(CartActionState.Complete);
    setIsLoading(false);

    await updateCart(updatedCart);

    trackAddToCart(addToCartTrackingData);
    postAlgoliaAddToCart(productSku, getInsight(productSku));
    klaviyoAddedToCart(updatedCart);
  };

  return (
    <>
      <ActionButton
        label="Add to cart"
        onClick={addToCart}
        isLoading={isLoading}
        state={buttonState}
        actionFinishedLabel="Added!"
        dataTestId="add-to-cart-button"
      />

      {shouldShowSlideout ? (
        <AttachAddToCartSlideout
          purchasedProduct={algoliaProduct}
          onClose={onModalClose}
          isOpen={modalVisible}
          pageType="pdp"
          shouldFetchServiceSuggestions={isEligibleForServices}
          freeBonusProducts={bonusProducts}
        />
      ) : null}
    </>
  );
};

export default AddToCart;
